import {
  CheckCircleFilled,
  CustomerServiceOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Input, Select, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { lineageDemoApi, LineageDemoScenario, LineageRehearsalFeedback } from './lineageDemoApi';

const { TextArea } = Input;
const { Option } = Select;
const quickTags = ['confidence', 'evidence', 'field-trace', 'impact', 'usability'];

export const LineageDemoCoach: React.FC = () => {
  const [scenarios, setScenarios] = useState<LineageDemoScenario[]>([]);
  const [selectedId, setSelectedId] = useState('field-trust-audit');
  const [turnIndex, setTurnIndex] = useState(0);
  const [feedback, setFeedback] = useState<LineageRehearsalFeedback[]>([]);
  const [message, setMessage] = useState('');
  const [source, setSource] = useState<LineageRehearsalFeedback['source']>('simulated-roleplay');
  const [tags, setTags] = useState<string[]>(['confidence']);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    void Promise.all([lineageDemoApi.demoScenarios(), lineageDemoApi.rehearsalFeedback()]).then(([nextScenarios, nextFeedback]) => {
      if (!active) return;
      setScenarios(nextScenarios);
      setFeedback(nextFeedback);
      if (nextScenarios.length && !nextScenarios.some(item => item.id === selectedId)) setSelectedId(nextScenarios[0].id);
    });
    return () => { active = false; };
  }, [selectedId]);

  const scenario = useMemo(() => scenarios.find(item => item.id === selectedId) ?? scenarios[0], [scenarios, selectedId]);
  const visibleTurns = scenario?.turns.slice(0, turnIndex + 1) ?? [];
  const complete = Boolean(scenario && turnIndex >= scenario.turns.length - 1);

  const submit = async () => {
    if (!scenario || message.trim().length < 5) return;
    setSaving(true);
    setSaved(false);
    try {
      const item = await lineageDemoApi.submitRehearsalFeedback({ scenarioId: scenario.id, source, message, tags });
      setFeedback(current => [item, ...current]);
      setMessage('');
      setSaved(true);
    } finally { setSaving(false); }
  };

  if (!scenario) return <div className="lineage-demo-loading"><Spin /><span>Loading rehearsal scenarios…</span></div>;

  return (
    <div className="lineage-demo-coach">
      <header><span><PlayCircleOutlined /> DEMO REHEARSAL</span><em>Preset role simulation</em></header>
      <Select aria-label="Demo scenario" value={scenario.id} onChange={value => { setSelectedId(value); setTurnIndex(0); setSaved(false); }}>
        {scenarios.map(item => <Option key={item.id} value={item.id}>{item.title}</Option>)}
      </Select>
      <p className="lineage-demo-goal">{scenario.customerGoal}</p>
      <div className="lineage-demo-personas">
        <span><CustomerServiceOutlined /><b>{scenario.presalesPersona.name}</b><small>{scenario.presalesPersona.role}</small></span>
        <span><UserOutlined /><b>{scenario.clientPersona.name}</b><small>{scenario.clientPersona.role}</small></span>
      </div>
      <div className="lineage-demo-dialogue">
        {visibleTurns.map(turn => <article key={turn.id} className={`is-${turn.speaker}`}><span>{turn.speaker === 'client' ? <UserOutlined /> : turn.speaker === 'presales' ? <CustomerServiceOutlined /> : <MessageOutlined />}{turn.label}</span><p>{turn.message}</p>{turn.expectedAction ? <small>UI move · {turn.expectedAction}</small> : null}</article>)}
      </div>
      <div className="lineage-demo-controls">
        <Button size="small" icon={<ReloadOutlined />} onClick={() => { setTurnIndex(0); setSaved(false); }}>Restart</Button>
        <Button size="small" type="primary" disabled={complete} onClick={() => setTurnIndex(index => Math.min(index + 1, scenario.turns.length - 1))}>{complete ? 'Scenario complete' : 'Next cue'}</Button>
      </div>

      <section className="lineage-feedback-capture">
        <h3>Capture client feedback</h3>
        <Select aria-label="Feedback evidence type" value={source} onChange={setSource}>
          <Option value="simulated-roleplay">Simulated roleplay</Option>
          <Option value="client-live">Client live feedback</Option>
        </Select>
        <TextArea aria-label="Client feedback" rows={3} value={message} placeholder="What proof, workflow or UI change did the client ask for?" onChange={event => { setMessage(event.target.value); setSaved(false); }} />
        <div className="lineage-feedback-tags">{quickTags.map(tag => <button type="button" key={tag} className={tags.includes(tag) ? 'is-active' : ''} onClick={() => setTags(current => current.includes(tag) ? current.filter(item => item !== tag) : [...current, tag])}>{tag}</button>)}</div>
        <Button block type="primary" icon={<SendOutlined />} loading={saving} disabled={message.trim().length < 5} onClick={() => void submit()}>Send to Lineage AI v2 queue</Button>
        {saved ? <span className="lineage-feedback-saved"><CheckCircleFilled /> Feedback recorded as versioned evidence.</span> : null}
      </section>

      <section className="lineage-feedback-queue">
        <h3>Improvement queue ({feedback.length})</h3>
        {feedback.slice(0, 4).map(item => <article key={item.id}><span><em>{item.status}</em><b>{item.requestedVersion}</b></span><p>{item.message}</p><small>{item.source} · {item.tags.join(' · ')}</small></article>)}
        {!feedback.length ? <p>No feedback captured yet. Complete the roleplay and record the client's objection.</p> : null}
      </section>
    </div>
  );
};
